import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as accountSvc from '@core/services/accountService';
import { useAuthStore } from '@core/store/authStore';

const ACCOUNT_KEY = 'account';

export function useProfile() {
  const customerId = useAuthStore((s) => s.customer?.customer_id);
  return useQuery({
    queryKey: [ACCOUNT_KEY, 'profile', customerId],
    queryFn: async () => {
      const profile = await accountSvc.getProfile();
      // The header reads the customer persisted at login. Keep it in step with
      // the server profile so a name changed elsewhere (or since login) shows.
      const current = useAuthStore.getState().customer;
      if (current && profile && typeof profile === 'object') {
        useAuthStore.getState().setCustomer({
          ...current,
          first_name: profile.first_name ?? current.first_name,
          last_name: profile.last_name ?? current.last_name,
          email: profile.email ?? current.email,
          mobile: profile.mobile ?? current.mobile,
        });
      }
      return profile;
    },
    enabled: Boolean(customerId),
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [ACCOUNT_KEY, 'updateProfile'],
    mutationFn: accountSvc.updateProfile,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNT_KEY, 'profile'] });
      // Also nudge the auth store so the header / home greeting reflect the
      // new name without waiting for refetch.
      // The PATCH response may not echo the profile, so fall back to what was
      // sent (a FormData when a photo is attached), then to the current value.
      const current = useAuthStore.getState().customer ?? {};
      const fromResponse = data?.data && typeof data.data === 'object' ? data.data : null;
      const sent = variables instanceof FormData ? Object.fromEntries(variables) : variables;
      const pick = (key) => fromResponse?.[key] ?? sent?.[key] ?? current[key];
      useAuthStore.getState().setCustomer({
        ...current,
        first_name: pick('first_name'),
        last_name: pick('last_name'),
        email: pick('email'),
      });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationKey: [ACCOUNT_KEY, 'changePassword'],
    mutationFn: accountSvc.changePassword,
  });
}
export function useDeleteAccount() {
  return useMutation({
    mutationKey: [ACCOUNT_KEY, 'deleteAccount'],
    mutationFn: accountSvc.deleteAccount,
  });
}
